import {
  RiCloseLine,
  RiExternalLinkLine,
  RiLogoutBoxLine,
  RiMenuLine,
  RiUserLine,
} from '@remixicon/react';
import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';
import { toast } from '@/components/ui/sonner';
import { useHaulerCentralAuthContext, useLogout } from '@/lib';
import { ROUTES } from '../routes';
import { HaulerCentralLogoIcon } from './HaulerCentralLogo';

interface HaulerCentralHeaderProps {
  showNav?: boolean;
}

const navLinks = [
  { to: ROUTES.SEARCH, label: 'Find Haulers' },
  // { to: ROUTES.PRICE_CHECK, label: 'Price Check' },
  // { to: ROUTES.PRICING, label: 'Pricing' },
] as const;

export function HaulerCentralHeader({ showNav = true }: HaulerCentralHeaderProps) {
  const { user, isAuthenticated } = useHaulerCentralAuthContext();
  const logout = useLogout();
  const navigate = useNavigate();
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  const handleProfileSettings = () => {
    navigate('/profile-settings');
  };

  const handleLogout = async () => {
    setIsLoggingOut(true);
    try {
      await logout.mutateAsync();
      navigate(ROUTES.HOME);
    } catch (error) {
      console.error('Logout failed:', error);
      toast.error('Logout failed. Please try again.');
      navigate(ROUTES.LOGIN);
    } finally {
      setIsLoggingOut(false);
    }
  };

  const getInitials = (firstName?: string, lastName?: string, email?: string) => {
    if (firstName && lastName) {
      return `${firstName[0]}${lastName[0]}`.toUpperCase();
    }
    if (email) {
      return email.slice(0, 2).toUpperCase();
    }
    return '';
  };

  return (
    <header className="border-b bg-white sticky top-0 z-50">
      <div className="container mx-auto px-4 h-16 flex items-center justify-between">
        <Link to={ROUTES.HOME} className="flex items-center gap-2">
          <HaulerCentralLogoIcon className="h-8 w-8 text-primary" />
          <span className="text-xl font-bold text-foreground">HaulerCentral</span>
        </Link>

        {showNav && (
          <>
            {/* Desktop nav */}
            <nav className="hidden md:flex items-center gap-6">
              {navLinks.map((link) => (
                <Link
                  key={link.to}
                  to={link.to}
                  className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
                >
                  {link.label}
                </Link>
              ))}
              <a
                href="https://commanderai.com"
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors flex items-center gap-1"
              >
                Commander AI
                <RiExternalLinkLine className="w-3 h-3" />
              </a>

              {isAuthenticated && user ? (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button
                      type="button"
                      className="flex items-center gap-2 rounded-full hover:bg-muted/80 transition-colors p-1"
                    >
                      <Avatar className="h-8 w-8">
                        <AvatarFallback className="bg-primary text-primary-foreground text-xs font-medium">
                          {getInitials(user?.first_name, user?.last_name, user?.email)}
                        </AvatarFallback>
                      </Avatar>
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-56">
                    <DropdownMenuLabel>
                      <div className="flex flex-col space-y-1">
                        <p className="text-sm font-medium leading-none">
                          {user?.first_name && user?.last_name
                            ? `${user?.first_name} ${user?.last_name}`
                            : ''}
                        </p>
                        <p className="text-xs leading-none text-muted-foreground">
                          {user?.email || ''}
                        </p>
                      </div>
                    </DropdownMenuLabel>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={handleProfileSettings}>
                      <RiUserLine className="mr-2 h-4 w-4" />
                      Profile Settings
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      onClick={handleLogout}
                      disabled={isLoggingOut}
                      className="text-red-600 focus:text-red-600 focus:bg-red-50"
                    >
                      <RiLogoutBoxLine className="mr-2 h-4 w-4" />
                      {isLoggingOut ? 'Logging out...' : 'Log out'}
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              ) : (
                <Link
                  to={ROUTES.LOGIN}
                  className="text-sm font-medium text-foreground hover:text-primary transition-colors"
                >
                  Sign In
                </Link>
              )}
            </nav>

            {/* Mobile hamburger */}
            <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
              <SheetTrigger asChild>
                <button
                  type="button"
                  className="md:hidden p-2 -mr-2 text-muted-foreground hover:text-foreground transition-colors"
                  aria-label="Open menu"
                >
                  <RiMenuLine className="w-6 h-6" />
                </button>
              </SheetTrigger>
              <SheetContent side="right" className="w-72 p-0">
                <div className="flex flex-col h-full">
                  <div className="flex items-center justify-between px-4 h-16 border-b">
                    <span className="text-lg font-bold text-foreground">Menu</span>
                    <button
                      type="button"
                      onClick={() => setMobileOpen(false)}
                      className="p-2 -mr-2 text-muted-foreground hover:text-foreground transition-colors"
                      aria-label="Close menu"
                    >
                      <RiCloseLine className="w-5 h-5" />
                    </button>
                  </div>

                  <nav className="flex flex-col py-4">
                    {navLinks.map((link) => (
                      <Link
                        key={link.to}
                        to={link.to}
                        onClick={() => setMobileOpen(false)}
                        className="px-4 py-3 text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-colors"
                      >
                        {link.label}
                      </Link>
                    ))}
                    <a
                      href="https://commanderai.com"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="px-4 py-3 text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-colors flex items-center gap-1"
                    >
                      Commander AI
                      <RiExternalLinkLine className="w-3 h-3" />
                    </a>
                  </nav>

                  <div className="mt-auto border-t px-4 py-4">
                    {isAuthenticated && user ? (
                      <div className="flex flex-col gap-2">
                        <div className="flex items-center gap-3 pb-3 border-b">
                          <Avatar className="h-8 w-8">
                            <AvatarFallback className="bg-primary text-primary-foreground text-xs font-medium">
                              {getInitials(user?.first_name, user?.last_name, user?.email)}
                            </AvatarFallback>
                          </Avatar>
                          <div className="flex flex-col">
                            <p className="text-sm font-medium leading-none">
                              {user?.first_name && user?.last_name
                                ? `${user?.first_name} ${user?.last_name}`
                                : ''}
                            </p>
                            <p className="text-xs leading-none text-muted-foreground mt-1">
                              {user?.email || ''}
                            </p>
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => {
                            setMobileOpen(false);
                            handleProfileSettings();
                          }}
                          className="flex items-center gap-2 py-2 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
                        >
                          <RiUserLine className="h-4 w-4" />
                          Profile Settings
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            setMobileOpen(false);
                            handleLogout();
                          }}
                          disabled={isLoggingOut}
                          className="flex items-center gap-2 py-2 text-sm font-medium text-red-600 hover:text-red-700 transition-colors"
                        >
                          <RiLogoutBoxLine className="h-4 w-4" />
                          {isLoggingOut ? 'Logging out...' : 'Log out'}
                        </button>
                      </div>
                    ) : (
                      <Link
                        to={ROUTES.LOGIN}
                        onClick={() => setMobileOpen(false)}
                        className="block w-full text-center py-2.5 px-4 bg-primary text-primary-foreground text-sm font-medium rounded-lg hover:bg-primary/90 transition-colors"
                      >
                        Sign In
                      </Link>
                    )}
                  </div>
                </div>
              </SheetContent>
            </Sheet>
          </>
        )}
      </div>
    </header>
  );
}
